import { isNewFood, isSnusOrTobacco } from "components/product/ProductUpdateForm/fields/category";
import { IFoodlaCategoryOption } from "models/category";
import IProduct from "models/product";
import { IExchangeRates } from "models/exchange-rates";
import { PriceCurrencyEnum } from "models/producer";
import { toSafeFloat } from './helpers.js';

// Apotea effective VAT: food (not snus/tobacco) stored as 12 is effectively 6%
// (temporary Swedish food VAT). Mirrors apotea-export getVat
// (coop-assortment/apps/apotea-export/src/fields/Vat.ts). Pure — the caller
// supplies the already-loaded category tree.
//
// TEMPORARY: when the reduced food VAT ends (food back to 12%), remove the
// `=== 12 → 6` branch below — keep it in sync with apotea-export getVat, which
// must change in the same release. Removing it also makes the category tree
// unnecessary here (getEffectiveApoteaVat returns the stored rate), so the
// caller's `vat === 12` tree load + `rootCategory` arg can be dropped too.
export const getEffectiveApoteaVat = (
  product?: IProduct,
  rootCategory?: IFoodlaCategoryOption,
): number | null | undefined => {
  const vat = product?.basicDataTaxClassification;
  if (vat !== 12) return vat;
  if (
    isNewFood(product?.foodlaCategory, rootCategory) &&
    !isSnusOrTobacco(product?.foodlaCategory, rootCategory)
  ) {
    return 6;
  }
  return vat;
};

// Apotea gross margin %, rounded to 1 decimal:
//   (utpris_exkl − inköpspris) / utpris_exkl, with utpris_exkl = storePrice / (1 + vat/100).
// Returns null when a required input is missing — a price absent, the VAT rate
// unknown, or a non-positive ex-VAT price — so the card shows "—" rather than a
// wrong number. (A known 0 % rate is valid and computes normally.)
export const computeApoteaMarginPercent = (
  storePrice?: number | null,
  purchasePrice?: number | null,
  vat?: number | null,
): number | null => {
  if (storePrice == null || purchasePrice == null) return null;
  if (vat == null) return null;
  const utprisExkl = storePrice / (1 + vat / 100);
  if (!Number.isFinite(utprisExkl) || utprisExkl <= 0) return null;
  const percent = ((utprisExkl - purchasePrice) / utprisExkl) * 100;
  return Math.round(percent * 10) / 10;
};

// Formats margin percentage using Swedish decimal comma, a space before the % sign,
// and an em-dash (—) for null.
export const formatApoteaMargin = (margin: number | null): string => {
  if (margin == null) return '—';
  return `${margin.toFixed(1).replace('.', ',')} %`;
};

export const getApoteaMarginSek = (
  product: IProduct,
  rootCategory?: IFoodlaCategoryOption,
  exchangeRates?: IExchangeRates | null
): { percent: number | null; date?: string; missingRateCurrency?: string } => {
  // No currency on the producer means SEK, as in the producer and product forms.
  const currency =
    product.producerApoteaPriceCurrency || product.producer?.apoteaPriceCurrency || PriceCurrencyEnum.SEK;

  let rate: number | undefined = undefined;
  if (currency === PriceCurrencyEnum.SEK) {
    rate = 1;
  } else {
    const currencyRate = exchangeRates?.rates[currency];
    if (currencyRate && exchangeRates?.rates.SEK) {
      rate = exchangeRates.rates.SEK / currencyRate;
    }
  }

  let purchasePrice: number | null = null;
  const parsedPurchasePrice = toSafeFloat(product.apoteaPurchasePrice, null);
  if (parsedPurchasePrice != null && rate != null) {
    purchasePrice = parsedPurchasePrice * rate;
  }

  let date: string | undefined = undefined;
  let missingRateCurrency: string | undefined = undefined;
  if (currency !== PriceCurrencyEnum.SEK) {
    if (rate != null) {
      date = exchangeRates?.publicationDate;
    } else {
      missingRateCurrency = currency;
    }
  }

  return {
    percent: computeApoteaMarginPercent(
      toSafeFloat(product.apoteaStorePrice, null),
      purchasePrice,
      getEffectiveApoteaVat(product, rootCategory)
    ),
    date,
    missingRateCurrency,
  };
};
